/*
 * cart-upsell-scope.js
 *
 * Scopes cart-drawer toggle upsells to the cart line they created.
 *
 * The vendor bundle (main.js) matches upsell cart lines by product handle
 * (`.cart-item--product-<handle>`), so it cannot tell a line an upsell added
 * from an identical line the shopper added themselves. Four routines act on
 * that match, all gated behind `cart-drawer-upsell[data-toggle="true"]`:
 *
 *   1. initToggleUpsells() -> addRemoveFromCart(), which deletes the matched
 *      line whenever the toggle is off (the default), wiping real cart items
 *      on page load.
 *   2. validateCartGifts() -> validateSingleQuantityCartItems(), which clamps
 *      the matched line to quantity 1 and deletes duplicate lines.
 *   3. getUpsellHandles() -> oneNonUpellRemaining() -> checkForClear(), which
 *      counts the shopper's line as an upsell and flips every remove button
 *      into "clear the whole cart".
 *   4. fixParsedHtml(), which carries toggle and variant state across cart
 *      re-renders.
 *
 * Upsell forms now tag their line with a hidden `_upsell` property, which the
 * cart item renderers turn into a `cart-item--upsell-<key>` class, and the
 * upsell element carries the same key in `data-upsell-key`. Cart-drawer
 * toggles render `data-toggle="false"` so routines 1-4 skip them entirely;
 * this file re-implements 1, 3 and 4 against the scoped class. Routine 2 is
 * intentionally dropped - the upsell's own line is always quantity 1, and the
 * shopper's line is none of its business.
 *
 * Gifts (<cart-drawer-gift>, which extends CartDrawerUpsell) and product-page
 * upsells carry no data-upsell-key and are passed through untouched.
 */
(function () {
  if (!window.customElements) return;

  var UpsellClass = customElements.get('cart-drawer-upsell');
  var DrawerClass = customElements.get('cart-drawer');
  var ItemsClass = customElements.get('cart-items');
  if (!UpsellClass || !DrawerClass || !ItemsClass) return;

  var upsellProto = UpsellClass.prototype;
  var drawerProto = DrawerClass.prototype;
  var itemsProto = ItemsClass.prototype;

  /* Keys the shopper switched off by hand this page view, so a preselected
     upsell isn't force-added back underneath them. */
  var optedOut = {};

  function ownLine(el) {
    var key = el.dataset.upsellKey;
    if (!key || !el.cartDrawer) return null;
    return el.cartDrawer.querySelector('.cart-item--upsell-' + key);
  }

  function disableControls(el) {
    if (el.toggleBtn) el.toggleBtn.setAttribute('disabled', '');
    el.variantSelectElements.forEach(function (select) {
      select.setAttribute('disabled', '');
    });
  }

  /* ---- 1. add / remove, scoped to this upsell's own line ---------------- */

  var originalAddToCart = upsellProto.addToCart;
  upsellProto.addToCart = function () {
    if (!this.dataset.upsellKey) return originalAddToCart.apply(this, arguments);
    delete optedOut[this.dataset.upsellKey];
    if (ownLine(this)) return;
    disableControls(this);
    this.productForm.handleSubmit();
  };

  var originalRemoveFromCart = upsellProto.removeFromCart;
  upsellProto.removeFromCart = function () {
    if (!this.dataset.upsellKey) return originalRemoveFromCart.apply(this, arguments);
    var line = ownLine(this);
    if (!line || !this.cartItems) return;
    optedOut[this.dataset.upsellKey] = true;
    disableControls(this);
    this.cartItems.updateQuantity(line.dataset.index, 0);
  };

  var originalGetUpdateRequired = upsellProto.getUpdateRequired;
  upsellProto.getUpdateRequired = function () {
    if (!this.dataset.upsellKey) return originalGetUpdateRequired.apply(this, arguments);
    var line = ownLine(this);
    if (line && this.dataset.selected === 'false') return true;
    return !line && this.dataset.selected === 'true';
  };

  /* ---- 3. clear-cart accounting ---------------------------------------- */

  drawerProto.oneNonUpellRemaining = function () {
    var items = this.querySelectorAll('.cart-item');
    var handles = this.upsellHandles || [];
    var upsellCount = 0;

    items.forEach(function (item) {
      if (item.className.indexOf('cart-item--upsell-') !== -1) {
        upsellCount++;
        return;
      }
      for (var i = 0; i < handles.length; i++) {
        if (item.classList.contains('cart-item--product-' + handles[i])) {
          upsellCount++;
          return;
        }
      }
    });

    return items.length - upsellCount <= 1;
  };

  /* checkForClear() blanket-arms every remove button with clearCart once the
     last real product is the only thing left, so removing it doesn't strand
     orphan upsells. That is right for a shopper's own line, but an upsell's
     own line is removable too once own_line_display is "The cart item only"
     or "Both" - and clicking its bin should remove that line, not the cart.
     Arm the flag everywhere except on marked upsell lines. */
  drawerProto.checkForClear = function () {
    var clear = this.oneNonUpellRemaining();
    this.querySelectorAll('cart-remove-button').forEach(function (button) {
      var line = button.closest('.cart-item');
      var isUpsellLine = !!line && line.className.indexOf('cart-item--upsell-') !== -1;
      button.clearCart = clear && !isUpsellLine;
    });
  };

  /* ---- 4. carry the shopper's variant choice across a cart re-render ---- */

  function snapshotUpsells(drawer) {
    var snapshot = {};
    drawer.querySelectorAll('cart-drawer-upsell[data-upsell-key]').forEach(function (el) {
      var picker = el.querySelector('.upsell__variant-picker');
      if (!picker) return;
      var image = el.querySelector('.upsell__image__img');
      var price = el.querySelector('.upsell__price');
      snapshot[el.dataset.upsellKey] = {
        id: el.dataset.id,
        values: Array.prototype.map.call(picker.querySelectorAll('select'), function (select) {
          return select.value;
        }),
        image: image ? image.src : null,
        price: el.dataset.updatePrices === 'true' && price ? price.innerHTML : null,
      };
    });
    return snapshot;
  }

  function restoreUpsells(drawer, snapshot) {
    drawer.querySelectorAll('cart-drawer-upsell[data-upsell-key]').forEach(function (el) {
      var saved = snapshot[el.dataset.upsellKey];
      if (!saved) return;

      var picker = el.querySelector('.upsell__variant-picker');
      if (!picker) return;

      var selects = picker.querySelectorAll('select');
      if (selects.length !== saved.values.length) return;

      selects.forEach(function (select, index) {
        select.value = saved.values[index];
        select.querySelectorAll('option').forEach(function (option) {
          option.removeAttribute('selected');
          if (option.value === saved.values[index]) option.setAttribute('selected', '');
        });
      });

      if (saved.id) {
        el.dataset.id = saved.id;
        var idInput = el.querySelector('[name="id"]');
        if (idInput) idInput.value = saved.id;
      }

      var image = el.querySelector('.upsell__image__img');
      if (image && saved.image) image.src = saved.image;

      var price = el.querySelector('.upsell__price');
      if (price && saved.price !== null) price.innerHTML = saved.price;
    });
  }

  /* ---- 1 (cont.) preselected auto-add ----------------------------------- */

  /* `allowPreselect` is only true on page load and when the cart just went
     from empty to filled - the two moments the vendor bundle would have added
     a preselected upsell (via initToggleUpsells and via the batched
     add-to-cart payload respectively). Running it on every re-render would
     fight the shopper each time they switched a preselected upsell back off.
     `data-selected` itself is rendered from the cart by upsell-block.liquid,
     so it is already correct here; this only covers the auto-add. */
  function syncScopedUpsells(drawer, allowPreselect) {
    drawer.querySelectorAll('cart-drawer-upsell[data-upsell-key]').forEach(function (el) {
      if (!el.addToCart) return;

      if (ownLine(el)) {
        el.dataset.selected = 'true';
        return;
      }

      var stillPreselected =
        el.dataset.preselected === 'true' && !optedOut[el.dataset.upsellKey];

      /* An empty cart is the one state where the vendor bundle also left a
         preselected toggle switched on without a cart line behind it. */
      if (stillPreselected && drawer.classList.contains('is-empty')) return;

      if (allowPreselect && stillPreselected) {
        el.dataset.selected = 'true';
        el.addToCart();
        return;
      }

      el.dataset.selected = 'false';
    });
  }

  function afterCartRender(drawer, snapshot, wasEmpty) {
    restoreUpsells(drawer, snapshot);
    /* upsellHandles is only computed in the CartDrawer constructor, so it goes
       stale the moment the drawer re-renders - refresh it before re-checking. */
    if (drawer.getUpsellHandles) drawer.upsellHandles = drawer.getUpsellHandles();
    syncScopedUpsells(drawer, wasEmpty);
    if (drawer.checkForClear) drawer.checkForClear();
  }

  /* CartDrawerItems.updateCart() and the add-to-cart flow render through here. */
  var originalRenderContents = drawerProto.renderContents;
  drawerProto.renderContents = function () {
    var wasEmpty = !this.querySelector('.cart-item');
    var snapshot = snapshotUpsells(this);
    var result = originalRenderContents.apply(this, arguments);
    afterCartRender(this, snapshot, wasEmpty);
    return result;
  };

  /* Quantity changes and line removals render through CartItems.updateQuantity
     instead, which replaces the drawer markup itself rather than going through
     CartDrawer.renderContents. It is async, so chain onto its promise - that
     also puts this after its `finally` block. */
  var originalUpdateQuantity = itemsProto.updateQuantity;
  itemsProto.updateQuantity = function () {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return originalUpdateQuantity.apply(this, arguments);

    var wasEmpty = !drawer.querySelector('.cart-item');
    var snapshot = snapshotUpsells(drawer);

    return Promise.resolve(originalUpdateQuantity.apply(this, arguments)).then(function (result) {
      afterCartRender(drawer, snapshot, wasEmpty);
      return result;
    });
  };

  /* checkForClear() already ran once in the CartDrawer constructor, against the
     unpatched accounting - re-run it now that the scoped version is in place. */
  var initialDrawer = document.querySelector('cart-drawer');
  if (initialDrawer) {
    syncScopedUpsells(initialDrawer, true);
    if (initialDrawer.checkForClear) initialDrawer.checkForClear();
  }
})();
